import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditCard, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Credits() {
  const { getAuthHeaders } = useAuth();
  const [credits, setCredits] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [showPayment, setShowPayment] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [summary, setSummary] = useState({ total_pending: 0, total_balance: 0 });

  useEffect(() => { fetchCredits(); fetchSummary(); }, [filter]);

  const fetchCredits = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const res = await axios.get(`${API}/api/credits${params}`, { headers: getAuthHeaders() });
      setCredits(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSummary = async () => {
    try {
      const res = await axios.get(`${API}/api/credits/summary`, { headers: getAuthHeaders() });
      setSummary(res.data);
    } catch (err) { console.error(err); }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (!selectedCredit) return;
    try {
      await axios.post(`${API}/api/credits/${selectedCredit.id}/payment`, {
        amount: parseFloat(paymentAmount),
        method: paymentMethod
      }, { headers: getAuthHeaders() });
      setShowPayment(false);
      setPaymentAmount('');
      fetchCredits();
      fetchSummary();
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="credits-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Creditos</h1>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{summary.total_pending}</p>
              <p className="text-sm text-slate-500">Creditos pendientes</p>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">${summary.total_balance?.toFixed(2)}</p>
              <p className="text-sm text-slate-500">Saldo total pendiente</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setFilter('pending')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === 'pending' ? 'bg-[#1D3C6E] text-white' : 'bg-slate-100 text-slate-600'}`}>Pendientes</button>
        <button onClick={() => setFilter('paid')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === 'paid' ? 'bg-[#1D3C6E] text-white' : 'bg-slate-100 text-slate-600'}`}>Pagados</button>
        <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!filter ? 'bg-[#1D3C6E] text-white' : 'bg-slate-100 text-slate-600'}`}>Todos</button>
      </div>

      {/* Credits Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Cliente</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Monto Original</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Pagado</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Saldo</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Estado</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {credits.map((credit) => (
              <tr key={credit.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{credit.client_name}</td>
                <td className="px-4 py-3">${credit.original_amount?.toFixed(2)}</td>
                <td className="px-4 py-3 text-green-600">${credit.amount_paid?.toFixed(2)}</td>
                <td className="px-4 py-3 font-bold text-red-600">${credit.balance?.toFixed(2)}</td>
                <td className="px-4 py-3">
                  <span className={`status-badge ${credit.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                    {credit.status === 'paid' ? 'Pagado' : 'Pendiente'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {credit.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => { setSelectedCredit(credit); setShowPayment(true); }} className="text-xs h-7" data-testid={`pay-credit-${credit.id}`}>
                      Abonar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {credits.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No hay creditos</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Registrar Abono</DialogTitle></DialogHeader>
          {selectedCredit && (
            <form onSubmit={handlePayment} className="space-y-3">
              <p className="text-sm text-slate-600">Saldo pendiente: <span className="font-bold text-red-600">${selectedCredit.balance?.toFixed(2)}</span></p>
              <div><label className="text-xs font-medium text-slate-600">Monto del abono *</label><Input type="number" step="0.01" max={selectedCredit.balance} value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} required /></div>
              <div>
                <label className="text-xs font-medium text-slate-600">Metodo de pago</label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Efectivo">Efectivo</SelectItem>
                    <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="Transferencia">Transferencia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]">Registrar Pago</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
